import React from 'react';
import { withPropsOnChange } from 'recompose';
import { Column, Grid } from '../../components/layout/grid';
import { TBody, TComponent } from '../../components/template';
import { getSectionsList } from '../../helpers/custom-page/sections';
import styles from '../../styles/templates/custom-page/custom-template.module.scss';
import { CustomPageWidget as Widget } from '../../widgets';
import { DigitalCoachWidget } from '../../widgets/digital-coach-widget';
import { PointsMessage } from '../../widgets/points-widget/components/points-message';
import { ContentRepeater } from '../../widgets/repeater/content-repeater';
import { ActivityProgress } from '../../components/activity-progress';
import { ManualCoach } from './components/coach';
import { FAQ } from './components/faq';
import { FeaturedArticles } from './components/featured-articles';
import { CustomPageImage } from './components/image';
import { TrustCard } from './components/trust-card';
import { withCoachieTitle } from './hocs/withCoachieTitle';

export const CustomPageWidget = React.memo(({ name, children, widgets, ...rest }) => (
  <Widget name={name} widgets={widgets} {...rest}>
    {children}
  </Widget>
));

const CustomPageBody = React.memo(({ html }) => <TBody renderAs='section' html={html} />);

const CustomPageFAQ = React.memo(({ items, id }) => (
  <TComponent renderAs='section' id={id}>
    <FAQ items={items} />
  </TComponent>
));

const CustomPagePoints = React.memo(({ activity, data, id }) => {
  const {
    POINTS: {
      COMMUNICATIONS: {
        START_PAGE: { text }
      }
    }
  } = window.CONFIG;

  return (
    <TComponent renderAs='section' id={id} edge>
      <PointsMessage activity={activity} text={text} points={data} />
    </TComponent>
  );
});

const CoachieComponent = props => {
  switch (props.display) {
    case 'manual':
      return <ManualCoach {...props} />;
    case 'digital':
      return <DigitalCoachWidget />;
    default:
      return null;
  }
};

export const Coachie = withCoachieTitle(CoachieComponent);

const CustomPageCoach = React.memo(({ type, data, id }) => {
  switch (type) {
    case 'LandingPage':
      return (
        <TComponent renderAs='section' id={id}>
          <Coachie {...data} />
        </TComponent>
      );
    default:
      return (
        <TComponent renderAs='section' edge className={styles.coach} id={id}>
          <Grid>
            <Column>
              <Coachie {...data} />
            </Column>
          </Grid>
        </TComponent>
      );
  }
});

const CustomPageTrustCard = React.memo(({ logo, valueAlignment, commercialDeal, id, ...rest }) => (
  <TComponent renderAs='section' id={id}>
    <TrustCard image={logo} firstBar={valueAlignment} secondBar={commercialDeal} {...rest} />
  </TComponent>
));

const CustomPageFeaturedArticles = React.memo(props => (
  <TComponent renderAs='section' id={props.id} edge>
    <FeaturedArticles {...props} />
  </TComponent>
));

const CustomActivityProgress = React.memo(props => (
  <TComponent renderAs='section' id={props.id} edge>
    <ActivityProgress {...props} />
  </TComponent>
));

export const CustomPageSection = React.memo(({ name, data, activity, type, children, widgets, id }) => {
  switch (name) {
    case 'widget':
      return (
        <CustomPageWidget widgets={widgets} {...data}>
          {children}
        </CustomPageWidget>
      );
    case 'image':
      return <CustomPageImage {...data} />;
    case 'body':
      return <CustomPageBody html={data.html ?? data} />;
    case 'faq':
      return <CustomPageFAQ items={data} id={id} />;
    case 'points':
      return <CustomPagePoints activity={activity} data={data} id={id} />;
    case 'coachie':
      return <CustomPageCoach type={type} data={data} id='coach' />;
    case 'trustCard':
      return <CustomPageTrustCard {...data} id={id} />;
    case 'contentBlock':
      return <CustomPageFeaturedArticles {...data} id={id} />;
    case 'activityProgress':
      return <CustomActivityProgress id={id} {...data} />;
    case 'repeater':
      return <ContentRepeater {...data} id={data.repeaterKey} />;
    default:
      return null;
  }
});

export const withSections = Component =>
  withPropsOnChange(['currentPage', 'faqs', 'statusesPlain', 'promotions'], props => {
    const sections = getSectionsList(props);

    return { sections };
  })(Component);
